import React, { useEffect } from 'react';
import { Grid, Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import MessageIcon from '@mui/icons-material/Message';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // For Costs
import VpnKeyIcon from '@mui/icons-material/VpnKey'; // For API Keys

import StatCard from '../components/StatCard';
import { useAdminApi } from '../hooks/useAdminApi';
import { AdminOverviewResponse } from '../types/adminApiTypes';

const OverviewView: React.FC = () => {
  const { data, loading, error, fetchData } = useAdminApi<AdminOverviewResponse>();

  // Get the current user to check if they are an admin
  const { user } = useSelector((state: RootState) => state.user);
  const isAdmin = user?.userType === 'admin';

  useEffect(() => {
    // Pass the endpoint identifier in the params object
    fetchData({ endpoint: '/overview' });
  }, [fetchData]);

  if (error) {
    return <Alert severity="error">Error loading overview data: {error}</Alert>;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="div">
          Dashboard
        </Typography>
        {isAdmin && (
          <Button
            component={RouterLink}
            to="/dashboard/users/create"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create New User
          </Button>
        )}
      </Box>
      <Grid container spacing={3}>
        {/* Row 1: Core Platform Metrics */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Users"
            value={data?.total_users}
            icon={<PeopleIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Organizations"
            value={data?.total_organizations}
            icon={<BusinessIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total User Messages"
            value={data?.total_user_messages}
            icon={<MessageIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>

        {/* Row 2: Costs & APIs */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Costs"
            value={data?.total_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total APIs"
            value={data?.total_APIs}
            icon={<VpnKeyIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewView;
